import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  TextField,
  Typography,
  withStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "../components/Button";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { TICKET_TRAFFIC_LIGHT_FEED } from "../containers/Tickets/Queries";
import { UPDATE_TICKET_TRAFFIC_LIGHT_CONFIG } from "../containers/Tickets/Mutations";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

const DEFAULT_RANGES = [
  { minDays: 0, maxDays: 10, color: "#28a745" },
  { minDays: 11, maxDays: 20, color: "#ffc107" },
  { minDays: 21, maxDays: 30, color: "#fd7e14" },
  { minDays: 31, maxDays: 50, color: "#dc3545" },
];

const styles = (theme) => ({
  root: { margin: 0, padding: theme.spacing(2) },
  closeButton: { position: "absolute", right: theme.spacing(1), top: theme.spacing(1), color: theme.palette.grey[500] },
  rangeRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 8 },
  colorSwatch: { width: 32, height: 32, borderRadius: "50%", border: "2px solid #ddd", cursor: "pointer", flexShrink: 0 },
  colorInput: { opacity: 0, position: "absolute", top: 0, left: 0, width: 32, height: 32, cursor: "pointer" },
  previewRow: { display: "flex", alignItems: "center", gap: 6 },
});

const TrafficLightConfigModal = withStyles(styles)(({ classes, modalStatus, setModalStatus }) => {
  const { t } = useTranslation("tickets");
  const [enabled, setEnabled] = useState(false);
  const [ranges, setRanges] = useState(DEFAULT_RANGES);

  const { data: ticketData, loading } = useQuery(TICKET_TRAFFIC_LIGHT_FEED, {
    variables: { where: { _id: modalStatus.ticket } },
    skip: !modalStatus.open || !modalStatus.ticket,
    fetchPolicy: "network-only",
  });

  const [updateConfig, { loading: saving }] = useMutation(UPDATE_TICKET_TRAFFIC_LIGHT_CONFIG);

  useEffect(() => {
    if (ticketData?.ticket?.trafficLightConfig) {
      const cfg = ticketData.ticket.trafficLightConfig;
      setEnabled(cfg.enabled || false);
      setRanges(cfg.ranges?.length ? cfg.ranges.map(r => ({ ...r })) : DEFAULT_RANGES);
    } else if (ticketData) {
      setEnabled(false);
      setRanges(DEFAULT_RANGES);
    }
  }, [ticketData]);

  const handleClose = () => setModalStatus({ open: false, ticket: null, app: null });

  const handleRangeChange = (idx, field, value) => {
    setRanges(prev => {
      const updated = prev.map((r, i) => i === idx ? { ...r, [field]: field === "color" ? value : Number(value) } : r);
      if (field === "maxDays" && idx < updated.length - 1) {
        updated[idx + 1] = { ...updated[idx + 1], minDays: Number(value) + 1 };
      }
      return updated;
    });
  };

  const getRangeErrors = () => ranges.map((range, idx) => {
    const errors = {};
    if (range.minDays < 0) errors.minDays = "No puede ser negativo";
    if (range.maxDays < 0) errors.maxDays = "No puede ser negativo";
    if (idx > 0 && range.minDays <= ranges[idx - 1].maxDays)
      errors.minDays = `Debe ser mayor a ${ranges[idx - 1].maxDays}`;
    if (range.maxDays <= range.minDays)
      errors.maxDays = `Debe ser mayor a ${range.minDays}`;
    return errors;
  });

  const rangeErrors = getRangeErrors();
  const hasErrors = rangeErrors.some(e => Object.keys(e).length > 0);

  const handleSave = async () => {
    try {
      await updateConfig({
        variables: {
          data: {
            ticketId: modalStatus.ticket,
            app: modalStatus.app,
            enabled,
            ranges: ranges.map(({ minDays, maxDays, color }) => ({ minDays, maxDays, color })),
          },
        },
      });
      Swal.fire({ icon: "success", title: "Guardado", text: "Configuración del semáforo actualizada.", timer: 2000, showConfirmButton: false });
      handleClose();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo guardar la configuración." });
    }
  };

  return (
    <Dialog open={modalStatus.open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.root}>
        <Typography variant="h6">Configuración del Semáforo</Typography>
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Typography variant="body2" color="textSecondary">Cargando...</Typography>
        ) : (
          <>
            <GridContainer style={{ marginBottom: 16 }}>
              <GridItem xs={12} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} color="primary" />
                <Typography variant="body1">{enabled ? "Semáforo activado" : "Semáforo desactivado"}</Typography>
              </GridItem>
            </GridContainer>

            <Typography variant="subtitle2" style={{ marginBottom: 8, color: "#666" }}>
              Rangos de días
            </Typography>

            {ranges.map((range, idx) => (
              <div key={idx} className={classes.rangeRow}>
                <TextField
                  label="Días inicio"
                  type="number"
                  value={range.minDays}
                  onChange={(e) => handleRangeChange(idx, "minDays", e.target.value)}
                  size="small"
                  variant="outlined"
                  style={{ width: 110 }}
                  disabled={!enabled}
                  error={!!rangeErrors[idx]?.minDays}
                  helperText={rangeErrors[idx]?.minDays || " "}
                  inputProps={{ min: 0 }}
                />
                <Typography variant="body2">—</Typography>
                <TextField
                  label="Días fin"
                  type="number"
                  value={range.maxDays}
                  onChange={(e) => handleRangeChange(idx, "maxDays", e.target.value)}
                  size="small"
                  variant="outlined"
                  style={{ width: 110 }}
                  disabled={!enabled}
                  error={!!rangeErrors[idx]?.maxDays}
                  helperText={rangeErrors[idx]?.maxDays || " "}
                  inputProps={{ min: 0 }}
                />
                <div style={{ position: "relative" }}>
                  <div
                    className={classes.colorSwatch}
                    style={{ background: range.color, opacity: enabled ? 1 : 0.4 }}
                  />
                  <input
                    type="color"
                    className={classes.colorInput}
                    value={range.color}
                    onChange={(e) => handleRangeChange(idx, "color", e.target.value)}
                    disabled={!enabled}
                  />
                </div>
                <div className={classes.previewRow} style={{ opacity: enabled ? 1 : 0.4 }}>
                  <span style={{ width: 12, height: 12, borderRadius: "50%", background: range.color, display: "inline-block" }} />
                  <Typography variant="caption" style={{ color: "#555" }}>
                    {range.minDays}–{range.maxDays} días
                  </Typography>
                </div>
              </div>
            ))}

            <GridContainer style={{ marginTop: 24, justifyContent: "flex-end" }}>
              <GridItem>
                <Button onClick={handleClose} style={{ marginRight: 8 }}>Cancelar</Button>
                <Button onClick={handleSave} color="beepquest" disabled={saving || hasErrors}>
                  {saving ? "Guardando..." : "Guardar"}
                </Button>
              </GridItem>
            </GridContainer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
});

export default TrafficLightConfigModal;
